/**
 * JobCardLineItems
 * -----------------------------------------------------------------------------
 * Tasks that the Service Manager has assigned to this job card. The
 * mechanic sees only Description + Quantity + a "mark complete" checkbox —
 * NEVER prices. The Service Manager can add new tasks manually, or pull
 * from a service-note / template library.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, ClipboardList, Wand2, CheckCircle2, GripVertical, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import SupplierCombobox from '@/components/invoices/SupplierCombobox';
// iter139.4 — Drag-and-drop reorder for tasks (same UX as the invoice
// line-item reorder). dnd-kit is already wired through the codebase.
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SortableTaskRow — wraps the task <li> with dnd-kit's useSortable hook
 * and exposes a render-prop so the parent decides where to attach the
 * GripVertical handle. Mirrors the SortableInvoiceRow pattern.
 */
function SortableTaskRow({ id, children, disabled }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 10 : undefined,
    background: isDragging ? '#f1f5f9' : undefined,
  };
  return children({ setNodeRef, attributes, listeners, style, isDragging });
}

export default function JobCardLineItems({ jobCard, user, onUpdated }) {
  const isMechanic = user && ['mechanic', 'technician'].includes(user.role);
  const isManager = user && ['super_admin', 'admin', 'service_manager', 'floor_manager'].includes(user.role);
  const isAssigned = jobCard?.assigned_to === user?.user_id;
  const canEditItems = isManager;
  // Quoted labour hours can also be edited by the assigned mechanic — workflow:
  // SM might leave it blank for the mechanic to estimate from the workshop
  // before SM conveys the quote to the customer.
  const canEditQuotedHours = isManager || (isMechanic && isAssigned);
  const canToggle = isManager || (isMechanic && isAssigned);

  const [items, setItems] = useState(jobCard?.line_items || []);
  // iter129 — SM may optionally capture supplier + ORP on a per-task basis
  // so the same row can later be promoted into the quote builder via the
  // structured mechanic findings flow. Fields are SM/FM/SA only — mechanic
  // role NEVER sees ORP. parts_supplier is free-text (e.g. "Repco" /
  // "Burson"); parts_cost_price is supplier cost in $.
  const [draft, setDraft] = useState({ description: '', quantity: 1, quoted_labour_hours: '', parts_supplier: '', parts_cost_price: '' });
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [pickingTpl, setPickingTpl] = useState('');
  // iter138.9c — Replace window.confirm() with an AlertDialog. window.confirm
  // inside a Radix Dialog is unreliable on iPad Safari (the prompt is rendered
  // behind the dialog, or never opens). Track the line targeted for deletion
  // here and show our own dialog with a 44pt tap target.
  const [deleteTarget, setDeleteTarget] = useState(null);

  // iter139.5 — Inline DESCRIPTION editor for SM/FM/SA. Other fields
  // (Hours, Supplier, ORP) are already editable in place; description
  // was previously read-only. Tracks which row is in edit mode so only
  // one row is editable at a time.
  const [editingLineId, setEditingLineId] = useState(null);
  const [editingDescription, setEditingDescription] = useState('');

  const startEditDescription = (it) => {
    setEditingLineId(it.line_id);
    setEditingDescription(it.description || '');
  };
  const cancelEditDescription = () => {
    setEditingLineId(null);
    setEditingDescription('');
  };
  const saveEditDescription = async () => {
    const trimmed = (editingDescription || '').trim();
    if (!trimmed) {
      toast.error('Task description cannot be empty.');
      return;
    }
    const line_id = editingLineId;
    setEditingLineId(null);
    setEditingDescription('');
    const currentRow = items.find(r => r.line_id === line_id);
    if (!currentRow || trimmed === currentRow.description) return;
    setItems(prev => prev.map(r => r.line_id === line_id ? { ...r, description: trimmed } : r));
    await updateLineField(line_id, { description: trimmed });
  };

  // Per-row inline edit of quoted_labour_hours — kept local until blur so the
  // SM can type without losing focus on every keystroke debounce.
  const updateQuotedHours = async (line_id, raw) => {
    const next = items.map(r =>
      r.line_id === line_id
        ? { ...r, quoted_labour_hours: raw === '' ? null : Number(raw) }
        : r,
    );
    setItems(next);  // optimistic
    await persist(next);
  };

  // iter139.4 — Drag-and-drop sensors. PointerSensor handles desktop
  // mouse; TouchSensor with a 150 ms long-press lets the iPad SM still
  // scroll the JC modal with a normal swipe. Keyboard sensor keeps the
  // feature a11y-friendly.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderTasks = async (activeId, overId) => {
    if (!activeId || !overId || activeId === overId) return;
    // Reorder the FULL items array but use the pendingItems order to find
    // the swap targets — the completed items remain pinned at the end of
    // the visible list and don't participate in the drag.
    const from = items.findIndex(r => r.line_id === activeId);
    const to = items.findIndex(r => r.line_id === overId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    // Re-number `order` so the new sequence persists end-to-end (the
    // backend echoes line_items in saved order on the JC PUT).
    const reordered = next.map((it, idx) => ({ ...it, order: idx }));
    setItems(reordered);   // optimistic
    await persist(reordered);
  };

  useEffect(() => { setItems(jobCard?.line_items || []); }, [jobCard]);

  useEffect(() => {
    if (!canEditItems) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/templates?category=service_note`, { credentials: 'include' });
        if (res.ok) {
          const d = await res.json();
          setTemplates(d.templates || d || []);
        }
      } catch (_e) { /* noop */ }
    })();
  }, [canEditItems]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line_items: next }),
      });
      if (res.ok) {
        const updated = await res.json();
        setItems(updated.line_items || next);
        onUpdated?.(updated);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } finally { setSaving(false); }
  };

  const addRow = async () => {
    const desc = draft.description.trim();
    if (!desc) return;
    const next = [
      ...items,
      {
        line_id: `jcl_${Math.random().toString(36).slice(2, 10)}`,
        description: desc,
        quantity: Number(draft.quantity) || 1,
        quoted_labour_hours: draft.quoted_labour_hours === '' ? null : Number(draft.quoted_labour_hours),
        // iter129 — SM-only optional fields. Mechanic users never see
        // these in the editor so they're always empty for their adds.
        parts_supplier: (draft.parts_supplier || '').trim() || null,
        parts_cost_price: draft.parts_cost_price === '' ? null : Number(draft.parts_cost_price),
        completed: false,
        source: 'manual',
      },
    ];
    await persist(next);
    setDraft({ description: '', quantity: 1, quoted_labour_hours: '', parts_supplier: '', parts_cost_price: '' });
  };

  // iter138.9c — Open AlertDialog instead of window.confirm(). Confirmation
  // is finalised by `confirmRemove()` below.
  const removeRow = (line_id) => {
    const row = items.find(r => r.line_id === line_id);
    if (!row) return;
    setDeleteTarget(row);
  };

  const confirmRemove = async () => {
    if (!deleteTarget) return;
    const line_id = deleteTarget.line_id;
    setDeleteTarget(null);
    await persist(items.filter(r => r.line_id !== line_id));
    toast.success('Task removed');
  };

  const toggleComplete = async (line_id, completed) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/line-items/toggle`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line_id, completed }),
      });
      if (res.ok) {
        const updated = await res.json();
        setItems(updated.line_items || []);
        onUpdated?.(updated);
      }
    } catch (_e) { /* noop */ }
  };

  // iter129 — Inline edit a single field on a task row (SM-only). Used
  // by the per-row Supplier + ORP inputs. Patches the local row first
  // for instant feedback, then PUTs the whole line_items array.
  const updateLineField = async (line_id, patch) => {
    const next = items.map(li => li.line_id === line_id ? { ...li, ...patch } : li);
    setItems(next);
    await persist(next);
  };

  // iter129 — Per-row OP/PO toggle. Same visual + same idempotent toggle
  // semantics as the JC-level pill on the wallet tile / kanban card.
  // Appends to parts_ordered_history on the line_item server-side.
  const toggleLinePartsOrdered = async (line_id) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}/line-items/${line_id}/toggle-parts-ordered`,
        { method: 'POST', credentials: 'include' },
      );
      if (res.ok) {
        const d = await res.json();
        // Local optimistic patch — backend echo confirms the new state.
        const next = items.map(li => li.line_id === line_id ? {
          ...li,
          parts_ordered: d.parts_ordered,
          parts_ordered_at: d.parts_ordered_at,
          parts_ordered_history: d.parts_ordered_history,
        } : li);
        setItems(next);
        toast.success(d.parts_ordered ? 'Marked ORDERED' : 'Order flag cleared');
        onUpdated?.({ ...jobCard, line_items: next });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not toggle');
      }
    } catch (_e) {
      toast.error('Network error');
    }
  };

  const insertFromTemplate = async (templateId) => {
    if (!templateId) return;
    const tpl = templates.find(t => t.template_id === templateId);
    if (!tpl) return;
    // Strip HTML, split lines into individual tasks
    const text = (tpl.content || '').replace(/<[^>]+>/g, '\n').replace(/&nbsp;/g, ' ');
    const lines = text.split(/\n+/).map(s => s.trim()).filter(s => s.length > 0 && s.length < 200);
    const rows = lines.map(l => ({
      line_id: `jcl_${Math.random().toString(36).slice(2, 10)}`,
      description: l,
      quantity: 1,
      completed: false,
      source: 'template',
    }));
    if (rows.length === 0) {
      toast.error('Template has no usable lines');
      return;
    }
    await persist([...items, ...rows]);
    setPickingTpl('');
    toast.success(`Added ${rows.length} task${rows.length === 1 ? '' : 's'} from "${tpl.name}"`);
  };

  const completedCount = items.filter(i => i.completed).length;
  const pendingItems = items.filter(i => !i.completed);
  const pendingCount = pendingItems.length;
  const totalQuotedHours = items.reduce(
    (s, i) => s + (Number(i.quoted_labour_hours) || 0),
    0,
  );
  const pendingQuotedHours = pendingItems.reduce(
    (s, i) => s + (Number(i.quoted_labour_hours) || 0),
    0,
  );

  return (
    <div className="border border-slate-200 rounded-lg" data-testid="jobcard-line-items">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b rounded-t-lg">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-slate-700" />
          <span className="text-sm font-semibold text-slate-800">Tasks for the mechanic</span>
          {items.length > 0 && (
            <span className="text-[10px] text-slate-500" data-testid="jcli-summary">
              {pendingCount === 0
                ? `All ${items.length} done`
                : `${pendingCount} pending${completedCount > 0 ? ` · ${completedCount} done (see Step 5)` : ''}`}
              {pendingQuotedHours > 0 && ` · ${pendingQuotedHours.toFixed(2)}h pending`}
              {totalQuotedHours > pendingQuotedHours && ` · ${totalQuotedHours.toFixed(2)}h total`}
            </span>
          )}
        </div>
        {canEditItems && templates.length > 0 && (
          <Select value={pickingTpl} onValueChange={insertFromTemplate}>
            <SelectTrigger className="h-8 text-xs w-44" data-testid="jcli-template-picker">
              <Wand2 className="h-3 w-3 mr-1" />
              <SelectValue placeholder="Insert from template…" />
            </SelectTrigger>
            <SelectContent>
              {templates.map(t => (
                <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {pendingItems.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400 text-center" data-testid="jcli-empty-pending">
          {items.length === 0
            ? (canEditItems
                ? 'No tasks yet. Add one below or pull from a template.'
                : 'No tasks assigned to you yet.')
            : 'All tasks complete — see Step 5 (Work Performed) for the history.'}
        </p>
      ) : (
        <DndContext
          sensors={dndSensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => reorderTasks(e.active?.id, e.over?.id)}
        >
          <SortableContext
            items={pendingItems.map(it => it.line_id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="divide-y divide-slate-100">
              {pendingItems.map(it => (
                <SortableTaskRow key={it.line_id} id={it.line_id} disabled={!canEditItems}>
                  {({ setNodeRef, attributes, listeners, style, isDragging }) => (
                    <li
                      ref={setNodeRef}
                      style={style}
                      className={`px-4 py-2.5 flex items-center gap-3 ${isDragging ? 'shadow-md' : ''}`}
                      data-testid={`jcli-row-${it.line_id}`}
                    >
                      {/* iter139.4 — Drag handle (SM/FM/SA only). 6-dot
                          GripVertical matches the invoice line-item UX
                          exactly. Long-press on iPad activates drag.
                          Hidden for mechanics (they can't reorder) AND
                          hidden while the row is in description-edit mode
                          (iter139.5) so the row is visually focused on
                          the Save / Cancel buttons. */}
                      {canEditItems && editingLineId !== it.line_id && (
                        <button
                          type="button"
                          {...attributes}
                          {...listeners}
                          className="text-slate-300 hover:text-slate-500 active:text-slate-700 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
                          title="Drag to reorder"
                          data-testid={`jcli-drag-${it.line_id}`}
                          aria-label="Drag to reorder task"
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                      )}
                      <Checkbox
                        checked={!!it.completed}
                        disabled={!canToggle || saving}
                        onCheckedChange={(v) => toggleComplete(it.line_id, !!v)}
                        data-testid={`jcli-toggle-${it.line_id}`}
                      />
              <div className="flex-1 min-w-0">
                {/* iter139.5 — Description: read-only by default; SM/FM/SA
                    can click the Edit (pencil) button to inline-edit. */}
                {editingLineId === it.line_id ? (
                  <div className="flex items-center gap-1.5" data-testid={`jcli-edit-mode-${it.line_id}`}>
                    <Input
                      autoFocus
                      value={editingDescription}
                      onChange={(e) => setEditingDescription(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); saveEditDescription(); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelEditDescription(); }
                      }}
                      className="h-8 text-sm flex-1 uppercase"
                      placeholder="TASK DESCRIPTION"
                      data-testid={`jcli-edit-input-${it.line_id}`}
                    />
                    <button
                      type="button"
                      onClick={saveEditDescription}
                      className="text-emerald-600 hover:text-emerald-800 active:bg-emerald-50 inline-flex items-center justify-center h-8 w-8 rounded-md transition-colors"
                      data-testid={`jcli-edit-save-${it.line_id}`}
                      title="Save (Enter)"
                      disabled={saving}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditDescription}
                      className="text-slate-400 hover:text-slate-600 active:bg-slate-50 inline-flex items-center justify-center h-8 w-8 rounded-md transition-colors"
                      data-testid={`jcli-edit-cancel-${it.line_id}`}
                      title="Cancel (Esc)"
                      disabled={saving}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <p className={`text-sm ${it.completed ? 'line-through text-slate-500' : 'text-slate-900 font-medium'} truncate`}>
                    {it.description}
                  </p>
                )}
                <p className="text-[10px] text-slate-400 flex items-center gap-1.5 flex-wrap">
                  <span>qty {it.quantity ?? 1}</span>
                  {it.completed && it.completed_at && (
                    <span className="text-emerald-600">
                      <CheckCircle2 className="inline h-3 w-3 mr-0.5" />
                      done {new Date(it.completed_at).toLocaleDateString()}
                    </span>
                  )}
                  {/* Mechanic view (read-only): show the green PO badge
                      when SM has flagged parts ordered for this task. */}
                  {!isManager && it.parts_ordered && (
                    <span
                      className="px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold uppercase text-[9px]"
                      title={`Parts ordered ${it.parts_ordered_at ? new Date(it.parts_ordered_at).toLocaleDateString() : ''}`}
                      data-testid={`jcli-po-ro-${it.line_id}`}
                    >
                      PO
                    </span>
                  )}
                  {/* iter138 — Mechanic view: surface the supplier name
                      (read-only, no ORP/$$) so the mechanic knows where
                      to collect the part from. Mirrors the print copy
                      so the iPad in-app view matches the printed JC. */}
                  {!isManager && it.parts_supplier && (
                    <span
                      className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold text-[10px]"
                      title="Parts supplier"
                      data-testid={`jcli-supplier-ro-${it.line_id}`}
                    >
                      @ {it.parts_supplier}
                    </span>
                  )}
                </p>
                {/* iter132 — SM-only inline editor row, now compact:
                    [Supplier (autocomplete from Supplier List)] [PO/OP pill]
                    [ORP $] [Hours]. Single horizontal strip so the SM can
                    enter all 4 fields without scanning across the row.
                    Mechanic role NEVER sees ORP / supplier; their Hours
                    input stays on the right side of the row (below). */}
                {isManager && (
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <SupplierCombobox
                      value={it.parts_supplier || ''}
                      onCommit={(v) => {
                        // iter139.4 — Persist ONLY on blur (or Enter), not
                        // on every keystroke. Eliminates the "text erased
                        // while typing" bug caused by every keystroke
                        // firing a PUT and the response racing back to
                        // overwrite the user's typed value.
                        const next = (v || '').trim() || null;
                        if (next !== (it.parts_supplier || null)) {
                          setItems(prev => prev.map(r => r.line_id === it.line_id ? { ...r, parts_supplier: next } : r));
                          updateLineField(it.line_id, { parts_supplier: next });
                        }
                      }}
                      placeholder="Supplier (e.g. Repco)"
                      className="flex-1 min-w-[160px] max-w-[260px] bg-emerald-50/40"
                      testId={`jcli-supplier-input-${it.line_id}`}
                    />
                    {/* OP/PO pill — same look + behaviour as JobCardItem.jsx
                        but scoped to this single task row. */}
                    <button
                      type="button"
                      onClick={() => toggleLinePartsOrdered(it.line_id)}
                      data-testid={`jcli-po-toggle-${it.line_id}`}
                      title={it.parts_ordered
                        ? `Ordered ${it.parts_ordered_at ? new Date(it.parts_ordered_at).toLocaleString() : ''} — click to clear`
                        : 'Click when you place the PO with the supplier'}
                      className={`
                        inline-flex items-center justify-center text-[10px] font-extrabold uppercase
                        w-9 h-7 rounded border transition-colors leading-none flex-shrink-0
                        ${it.parts_ordered
                          ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600'
                          : 'bg-rose-500 text-white border-rose-600 hover:bg-rose-600 animate-pulse'}
                      `}
                    >
                      {it.parts_ordered ? 'PO' : 'OP'}
                    </button>
                    <Input
                      type="number" step="0.01" min="0"
                      defaultValue={it.parts_cost_price ?? ''}
                      placeholder="ORP $"
                      className="h-8 text-xs w-20 text-right bg-emerald-50/40"
                      title="ORP — Our Real Price (supplier cost). SM / FM / SA only."
                      data-testid={`jcli-orp-input-${it.line_id}`}
                      onBlur={(e) => {
                        const v = e.target.value.trim() === '' ? null : Number(e.target.value);
                        if (v !== (it.parts_cost_price ?? null)) updateLineField(it.line_id, { parts_cost_price: v });
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    />
                    {/* Hours — moved INTO this row for SM so all 4 fields
                        sit together. Mechanic's Hours stays on the right. */}
                    <div className="flex items-center gap-1">
                      <Input
                        type="number" step="0.25" min="0"
                        defaultValue={it.quoted_labour_hours ?? ''}
                        placeholder="Hrs"
                        className={`h-8 w-16 text-xs text-right bg-blue-50/40 ${
                          it.quoted_labour_hours == null ? 'border-amber-300 ring-1 ring-amber-200' : ''
                        }`}
                        title={it.quoted_labour_hours == null
                          ? 'Quoted labour hours required before SM Review'
                          : 'Quoted labour hours for this task'}
                        data-testid={`jcli-quoted-${it.line_id}`}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          const next = v === '' ? null : Number(v);
                          const prev = it.quoted_labour_hours ?? null;
                          if (next !== prev) updateQuotedHours(it.line_id, v);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        disabled={saving}
                      />
                      <span className="text-[10px] text-slate-400">h</span>
                    </div>
                    {/* History dot — hover to see audit trail */}
                    {Array.isArray(it.parts_ordered_history) && it.parts_ordered_history.length > 0 && (
                      <span
                        className="text-[10px] text-slate-500 flex-shrink-0"
                        title={it.parts_ordered_history.slice(-4).map(h => `${h.to_state} · ${new Date(h.at).toLocaleString()} · ${h.by_name || ''}`).join('\n')}
                        data-testid={`jcli-po-history-${it.line_id}`}
                      >
                        ⓘ
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* Quoted labour hours — MECHANIC-only inline edit (SM/FM/SA
                  see this field above in the Supplier strip). Read-only
                  chip for non-editors. */}
              {!isManager && canEditQuotedHours ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    defaultValue={it.quoted_labour_hours ?? ''}
                    placeholder="hrs"
                    className={`w-16 h-8 text-sm text-right font-semibold bg-blue-50/40 ${
                      it.quoted_labour_hours == null ? 'border-amber-300 ring-1 ring-amber-200' : ''
                    }`}
                    title={it.quoted_labour_hours == null
                      ? 'Quoted labour hours required before SM Review'
                      : 'Quoted labour hours for this task'}
                    data-testid={`jcli-quoted-${it.line_id}`}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      const next = v === '' ? null : Number(v);
                      const prev = it.quoted_labour_hours ?? null;
                      if (next !== prev) updateQuotedHours(it.line_id, v);
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    disabled={saving}
                  />
                  <span className="text-[10px] text-slate-500 font-semibold">h</span>
                </div>
              ) : (
                !isManager && it.quoted_labour_hours != null && (
                  <span
                    className="text-[10px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded"
                    title="Quoted labour hours"
                    data-testid={`jcli-quoted-readonly-${it.line_id}`}
                  >
                    {Number(it.quoted_labour_hours).toFixed(2)}h
                  </span>
                )
              )}
              {canEditItems && editingLineId !== it.line_id && (
                <button
                  type="button"
                  onClick={() => startEditDescription(it)}
                  className="text-slate-400 hover:text-blue-600 active:bg-blue-50 active:text-blue-700 inline-flex items-center justify-center min-h-[40px] min-w-[40px] rounded-md transition-colors touch-manipulation"
                  data-testid={`jcli-edit-${it.line_id}`}
                  aria-label="Edit task description"
                  title="Edit task description"
                  disabled={saving}
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              {canEditItems && editingLineId !== it.line_id && (
                <button
                  type="button"
                  onClick={() => removeRow(it.line_id)}
                  className="text-slate-400 hover:text-rose-600 active:bg-rose-50 active:text-rose-700 inline-flex items-center justify-center min-h-[40px] min-w-[40px] rounded-md transition-colors touch-manipulation"
                  data-testid={`jcli-remove-${it.line_id}`}
                  aria-label="Remove task"
                  title="Remove task"
                  disabled={saving}
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </li>
                  )}
                </SortableTaskRow>
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {canEditItems && (
        <div className="border-t bg-white rounded-b-lg">
          {/* iter132 — single compact row: Description | Qty | Supplier | OP/PO toggle (added on save) | ORP | Hours | Add. Supplier autocompletes from /api/suppliers (Supplier List + past invoices). */}
          <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap">
            <Input
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value.toUpperCase() })}
              placeholder="ADD A TASK… E.G. REPLACE FRONT BRAKE PADS"
              className="flex-1 h-8 text-sm min-w-[200px] uppercase"
              data-testid="jcli-new-description"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRow(); } }}
            />
            <Input
              type="number"
              step="0.5"
              value={draft.quantity}
              onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
              className="w-14 h-8 text-sm text-right"
              title="Quantity"
              data-testid="jcli-new-quantity"
            />
            <div className="w-44">
              <SupplierCombobox
                value={draft.parts_supplier}
                onChange={(v) => setDraft({ ...draft, parts_supplier: v })}
                placeholder="Supplier (e.g. Repco)"
                className="bg-emerald-50/40"
                testId="jcli-new-supplier"
              />
            </div>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={draft.parts_cost_price}
              onChange={(e) => setDraft({ ...draft, parts_cost_price: e.target.value })}
              placeholder="ORP $"
              className="w-20 h-8 text-xs text-right bg-emerald-50/40"
              title="ORP — Our Real Price (supplier cost). SM / FM / SA only."
              data-testid="jcli-new-orp"
            />
            <Input
              type="number"
              step="0.25"
              min="0"
              value={draft.quoted_labour_hours}
              onChange={(e) => setDraft({ ...draft, quoted_labour_hours: e.target.value })}
              placeholder="Hrs"
              className="w-16 h-8 text-xs text-right bg-blue-50/40"
              title="Quoted labour hours"
              data-testid="jcli-new-quoted-hours"
            />
            <Button
              size="sm"
              onClick={addRow}
              disabled={!draft.description.trim() || saving}
              className="h-8"
              data-testid="jcli-add"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
          </div>
          <p className="px-4 pb-2 text-[10px] text-emerald-700/80">
            <span className="font-bold uppercase tracking-wider">SM only:</span> supplier auto-completes from your Supplier List · ORP $ is the supplier cost (cost to workshop). These initial tasks are <strong>not</strong> auto-pulled into the quote builder — only structured mechanic findings are.
          </p>
        </div>
      )}
      {/* iter138.9c — Touch-friendly confirm dialog. Replaces window.confirm()
          which was unreliable inside Radix Dialog on iPad Safari. */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="jcli-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this task?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.description
                ? <>
                    Remove <span className="font-semibold">"{deleteTarget.description}"</span> from this job card?
                    The mechanic will no longer see it.
                  </>
                : 'Remove this task from the job card?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="jcli-remove-cancel" className="min-h-[44px]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="min-h-[44px] bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-300"
              data-testid="jcli-remove-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
